import {
  EEX_MARKET_DATA_FORWARD_CURVE_SUBJECT_MAX,
  EexMarketDataForwardCurveRequestSchema,
  EexMarketDataForwardCurveResponseSchema,
  type EexMarketDataForwardCurveResponse,
  type EexMarketDataInstrument,
  type EexMarketDataTopOfBookItem,
} from "@chro/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { apiContract, ApiError } from "../../../lib/api";
import {
  eexForwardCurveDomainMonths,
  eexForwardCurveFirstExactMonthRange,
  eexForwardCurveHistorySubject,
  eexForwardCurveMonthDomain,
  eexForwardCurvePeriods,
  eexForwardCurvePeriodsInDomain,
  eexForwardCurvePrice,
  eexForwardCurveRollingDomain,
  eexForwardCurveTimeDomain,
  type EexForwardCurveHorizon,
  type EexForwardCurveMonthRange,
  type EexForwardCurvePriceBasis,
  type EexForwardCurveTimeDomain,
} from "../../../lib/eex-forward-curve";
import {
  buildEexMarketMatrix,
  type EexMatrixDeliveryPeriod,
} from "../../../lib/eex-live-market";
import { cn } from "../../../lib/utils";
import {
  ForwardCurveChart,
  type ForwardCurveChartSeries,
} from "./ForwardCurveChart";

type OverlayId =
  | "live"
  | "latest"
  | "fifteen-minutes"
  | "one-hour"
  | "one-day"
  | "one-week"
  | "settlement"
  | "custom";

type SnapshotOption = {
  id: Exclude<OverlayId, "live" | "settlement">;
  label: string;
  offsetMs: number | null;
  dash?: string;
  opacity: number;
};

const SNAPSHOT_OPTIONS: SnapshotOption[] = [
  { id: "one-week", label: "1 week ago", offsetMs: 7 * 24 * 60 * 60 * 1_000, dash: "3 5", opacity: 0.52 },
  { id: "one-day", label: "24 hours ago", offsetMs: 24 * 60 * 60 * 1_000, dash: "10 4 2 4", opacity: 0.6 },
  { id: "one-hour", label: "1 hour ago", offsetMs: 60 * 60 * 1_000, dash: "7 4", opacity: 0.72 },
  { id: "fifteen-minutes", label: "15 minutes ago", offsetMs: 15 * 60 * 1_000, dash: "2 3", opacity: 0.8 },
  { id: "latest", label: "Latest snapshot", offsetMs: 0, opacity: 0.9 },
  { id: "custom", label: "Custom snapshot", offsetMs: null, dash: "12 4", opacity: 0.72 },
];

const DEFAULT_OVERLAYS = new Set<OverlayId>(["live", "latest", "one-hour", "settlement"]);

type CustomHorizonRange = EexForwardCurveMonthRange;

export function ForwardCurveBoard({
  instruments,
  quotes,
  historyScopeIds,
}: {
  instruments: EexMarketDataInstrument[];
  quotes: EexMarketDataTopOfBookItem[];
  historyScopeIds: ReadonlySet<string>;
}) {
  const queryClient = useQueryClient();
  const matrix = useMemo(() => buildEexMarketMatrix(instruments, quotes), [instruments, quotes]);
  const [selectedGroupKey, setSelectedGroupKey] = useState("");
  const [basis, setBasis] = useState<EexForwardCurvePriceBasis>("midpoint");
  const [horizon, setHorizon] = useState<EexForwardCurveHorizon>("front");
  const [customHorizonDraft, setCustomHorizonDraft] = useState<CustomHorizonRange>({
    fromMonth: "",
    throughMonth: "",
  });
  const [customHorizonApplied, setCustomHorizonApplied] = useState<CustomHorizonRange | null>(null);
  const [customHorizonAnnouncement, setCustomHorizonAnnouncement] = useState("");
  const [overlays, setOverlays] = useState<Set<OverlayId>>(() => new Set(DEFAULT_OVERLAYS));
  const [anchorAt, setAnchorAt] = useState<string | null>(null);
  const [customAt, setCustomAt] = useState("");

  useEffect(() => {
    const refreshAnchor = () => setAnchorAt(new Date().toISOString());
    refreshAnchor();
    const timer = window.setInterval(refreshAnchor, 5 * 60 * 1_000);
    return () => window.clearInterval(timer);
  }, []);

  const selectedGroup = matrix.groups.find((group) => group.key === selectedGroupKey)
    ?? matrix.groups[0]
    ?? null;
  const availablePeriodKeys = useMemo(
    () => new Set(selectedGroup?.cells.keys() ?? []),
    [selectedGroup]
  );
  const frontPeriodCandidates = selectedGroup
    ? eexForwardCurvePeriods(matrix.periods, availablePeriodKeys, "front")
    : [];
  const rolling12MonthDomain = selectedGroup
    ? eexForwardCurveRollingDomain(matrix.periods, availablePeriodKeys, 12)
    : null;
  const rolling24MonthDomain = selectedGroup
    ? eexForwardCurveRollingDomain(matrix.periods, availablePeriodKeys, 24)
    : null;
  const appliedCustomDomain = customHorizonApplied
    ? eexForwardCurveMonthDomain(
      customHorizonApplied.fromMonth,
      customHorizonApplied.throughMonth
    )
    : null;
  const appliedCustomPeriods = selectedGroup && appliedCustomDomain
    ? eexForwardCurvePeriodsInDomain(matrix.periods, availablePeriodKeys, appliedCustomDomain)
    : [];
  const appliedCustomRangeOverflow = appliedCustomPeriods.length
    > EEX_MARKET_DATA_FORWARD_CURVE_SUBJECT_MAX;
  const displayDomain: EexForwardCurveTimeDomain | null = horizon === "12m"
    ? rolling12MonthDomain
    : horizon === "24m"
      ? rolling24MonthDomain
      : horizon === "custom"
        ? appliedCustomDomain
        : null;
  const domainPeriodCandidates = selectedGroup && displayDomain
    ? eexForwardCurvePeriodsInDomain(matrix.periods, availablePeriodKeys, displayDomain)
    : [];
  const selectedPeriodCandidates = displayDomain
    ? horizon === "custom"
      ? eexForwardCurvePeriods(matrix.periods, availablePeriodKeys, "custom", displayDomain)
      : domainPeriodCandidates.slice(0, EEX_MARKET_DATA_FORWARD_CURVE_SUBJECT_MAX)
    : selectedGroup
      ? eexForwardCurvePeriods(matrix.periods, availablePeriodKeys, horizon)
      : [];
  const draftCustomDomain = eexForwardCurveMonthDomain(
    customHorizonDraft.fromMonth,
    customHorizonDraft.throughMonth
  );
  const draftCustomPeriods = selectedGroup && draftCustomDomain
    ? eexForwardCurvePeriodsInDomain(matrix.periods, availablePeriodKeys, draftCustomDomain)
    : [];
  const customHorizonError = customHorizonValidationMessage(
    customHorizonDraft,
    draftCustomDomain,
    draftCustomPeriods.length
  );
  const customHorizonDirty = customHorizonApplied == null
    || customHorizonDraft.fromMonth !== customHorizonApplied.fromMonth
    || customHorizonDraft.throughMonth !== customHorizonApplied.throughMonth;
  const blockedDraftCustomRangeOverflow = horizon === "custom"
    && customHorizonApplied == null
    && draftCustomDomain != null
    && draftCustomPeriods.length > EEX_MARKET_DATA_FORWARD_CURVE_SUBJECT_MAX;
  const activeCustomRangeOverflow = horizon === "custom"
    && (appliedCustomRangeOverflow || blockedDraftCustomRangeOverflow);
  const activeCustomOverflowRange = appliedCustomRangeOverflow
    ? customHorizonApplied
    : blockedDraftCustomRangeOverflow
      ? customHorizonDraft
      : null;
  const activeCustomOverflowCount = appliedCustomRangeOverflow
    ? appliedCustomPeriods.length
    : blockedDraftCustomRangeOverflow
      ? draftCustomPeriods.length
      : 0;
  const curveCells = useMemo(() => {
    if (!selectedGroup) return [];
    const seenSubjects = new Set<string>();
    return selectedPeriodCandidates.flatMap((period) => {
      const cell = selectedGroup.cells.get(period.key);
      if (!cell) return [];
      const subjectKey = `${cell.instrument.apiFamily}\u0000${cell.instrument.subjectId}`;
      if (seenSubjects.has(subjectKey)) return [];
      seenSubjects.add(subjectKey);
      const subject = eexForwardCurveHistorySubject(cell.instrument, quotes, historyScopeIds);
      return [{ period, cell, subject }];
    });
  }, [
    historyScopeIds,
    quotes,
    selectedGroup,
    selectedPeriodCandidates.map((period) => period.key).join("|"),
  ]);
  const periods = curveCells.map((item) => item.period);
  const subjects = curveCells.flatMap(({ subject }) => subject ? [subject] : []);
  const effectiveDomain = displayDomain ?? eexForwardCurveTimeDomain(periods);
  const effectiveDomainMonths = eexForwardCurveDomainMonths(effectiveDomain);
  const domainPeriodOverflow = Math.max(
    0,
    domainPeriodCandidates.length - EEX_MARKET_DATA_FORWARD_CURVE_SUBJECT_MAX
  );

  useEffect(() => {
    setCustomHorizonAnnouncement("");
  }, [selectedGroup?.key]);

  const snapshotComparisons = useMemo<Array<{ id: string; requestedAt: string }>>(() => {
    if (!anchorAt) return [];
    const anchorMs = Date.parse(anchorAt);
    const comparisons: Array<{ id: string; requestedAt: string }> = [];
    for (const option of SNAPSHOT_OPTIONS) {
      if (!overlays.has(option.id)) continue;
      if (option.id === "custom") {
        const customMs = Date.parse(customAt);
        if (Number.isFinite(customMs)) {
          comparisons.push({ id: option.id, requestedAt: new Date(customMs).toISOString() });
        }
        continue;
      }
      comparisons.push({
        id: option.id,
        requestedAt: new Date(anchorMs - (option.offsetMs ?? 0)).toISOString(),
      });
    }
    return comparisons;
  }, [anchorAt, customAt, overlays]);
  const request = useMemo(() => subjects.length === 0
    ? null
    : EexMarketDataForwardCurveRequestSchema.parse({ subjects, snapshotComparisons }), [
    subjects.map((subject) => `${subject.scopeId}:${subject.subjectId}`).join("|"),
    snapshotComparisons.map((comparison) => `${comparison.id}:${comparison.requestedAt}`).join("|"),
  ]);
  const hasEnabledSnapshotOverlay = SNAPSHOT_OPTIONS.some((option) => overlays.has(option.id));
  const needsHistoricalRead = request != null
    && (!hasEnabledSnapshotOverlay || anchorAt != null)
    && (snapshotComparisons.length > 0 || overlays.has("settlement"));
  const historyQ = useQuery({
    queryKey: ["eex-market-data", "forward-curve", request],
    queryFn: ({ signal }) => {
      if (!request) throw new Error("Forward curve request is unavailable");
      return apiContract(
        EexMarketDataForwardCurveResponseSchema,
        "/eex/market-data/forward-curve/query",
        { method: "POST", body: JSON.stringify(request), signal }
      );
    },
    enabled: needsHistoricalRead,
    retry: false,
    staleTime: 30_000,
  });

  const series = buildChartSeries({
    curveCells,
    basis,
    overlays,
    snapshotComparisons,
    history: historyQ.data,
  });

  if (!selectedGroup) {
    return (
      <div className="rounded border border-border px-3 py-8 text-center text-xs text-muted">
        No curve-compatible products are available for the selected market filters.
      </div>
    );
  }

  const refresh = () => {
    setAnchorAt(new Date().toISOString());
    void queryClient.invalidateQueries({ queryKey: ["eex-market-data", "top-of-book"] });
  };
  const toggleOverlay = (id: OverlayId) => {
    if (id === "custom" && !overlays.has(id) && !customAt) {
      setCustomAt(toDatetimeLocal(new Date(Date.now() - 2 * 60 * 60 * 1_000)));
    }
    setOverlays((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const changeHorizon = (value: string) => {
    const next = value as EexForwardCurveHorizon;
    if (next === "custom") {
      const currentRange = eexForwardCurveDomainMonths(
        displayDomain
          ?? eexForwardCurveTimeDomain(selectedPeriodCandidates)
          ?? eexForwardCurveTimeDomain(frontPeriodCandidates)
      );
      const firstDeliveryMonth = frontPeriodCandidates
        .map((period) => period.deliveryStart?.slice(0, 7) ?? "")
        .find((month) => /^\d{4}-\d{2}$/.test(month));
      const candidates = [
        customHorizonApplied,
        currentRange,
        eexForwardCurveDomainMonths(rolling12MonthDomain),
        firstDeliveryMonth
          ? { fromMonth: firstDeliveryMonth, throughMonth: firstDeliveryMonth }
          : null,
      ];
      const seed = eexForwardCurveFirstExactMonthRange(
        matrix.periods,
        availablePeriodKeys,
        candidates,
        EEX_MARKET_DATA_FORWARD_CURVE_SUBJECT_MAX
      ) ?? candidates.find((candidate): candidate is CustomHorizonRange => candidate != null);
      if (seed) {
        setCustomHorizonDraft(seed);
        const seedDomain = eexForwardCurveMonthDomain(seed.fromMonth, seed.throughMonth);
        const seedCount = selectedGroup && seedDomain
          ? eexForwardCurvePeriodsInDomain(matrix.periods, availablePeriodKeys, seedDomain).length
          : 0;
        if (seedCount <= EEX_MARKET_DATA_FORWARD_CURVE_SUBJECT_MAX) {
          setCustomHorizonApplied(seed);
          setCustomHorizonAnnouncement(
            `Custom delivery horizon initialized to ${monthRangeLabel(seed)}.`
          );
        } else {
          setCustomHorizonApplied(null);
          setCustomHorizonAnnouncement("Narrow the proposed range before applying it.");
        }
      }
    } else {
      setCustomHorizonAnnouncement("");
    }
    setHorizon(next);
  };
  const changeCustomHorizon = (field: keyof CustomHorizonRange, value: string) => {
    setCustomHorizonDraft((current) => ({ ...current, [field]: value }));
    setCustomHorizonAnnouncement("");
  };
  const applyCustomHorizon = () => {
    if (!draftCustomDomain || customHorizonError) return;
    const next = { ...customHorizonDraft };
    setCustomHorizonApplied(next);
    setCustomHorizonAnnouncement(`Applied ${monthRangeLabel(next)}.`);
  };
  const cancelCustomHorizonChanges = () => {
    if (!customHorizonApplied) return;
    setCustomHorizonDraft(customHorizonApplied);
    setCustomHorizonAnnouncement("Unapplied delivery horizon changes were discarded.");
  };

  return (
    <div className="space-y-3">
      <div className="rounded border border-border/70 bg-border/[0.025] p-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-wrap items-end gap-2">
            <CurveSelect
              label="Area / product"
              value={selectedGroup.key}
              onChange={setSelectedGroupKey}
            >
              {matrix.groups.map((group) => (
                <option key={group.key} value={group.key}>
                  {group.label}{group.currency ? ` · ${group.currency}/${group.uom ?? "unit"}` : ""}
                </option>
              ))}
            </CurveSelect>
            <CurveSelect
              label="Live / snapshot basis"
              value={basis}
              onChange={(value) => setBasis(value as EexForwardCurvePriceBasis)}
            >
              <option value="midpoint">Midpoint</option>
              <option value="bid">Bid</option>
              <option value="ask">Ask</option>
              <option value="last">Last trade</option>
            </CurveSelect>
            <CurveSelect
              label="Delivery horizon"
              value={horizon}
              onChange={changeHorizon}
            >
              <option value="front">Front tenors</option>
              <option value="12m">Next 12 delivery months</option>
              <option value="24m">Next 24 delivery months</option>
              <option value="all">First 64 listed</option>
              <option value="custom">Custom range…</option>
            </CurveSelect>
          </div>
          <button
            type="button"
            onClick={refresh}
            className="flex h-8 items-center gap-1.5 rounded border border-border px-2 text-[10px] text-muted hover:border-accent hover:text-fg"
          >
            <RefreshCw size={12} className={historyQ.isFetching ? "animate-spin" : undefined} />
            Refresh comparisons
          </button>
        </div>

        {horizon === "custom" && (
          <fieldset className="mt-3 rounded border border-border/70 px-2.5 pb-2.5 pt-1.5">
            <legend className="px-1 text-[9px] font-medium uppercase tracking-wide text-muted">
              Custom delivery horizon
            </legend>
            <div className="flex flex-wrap items-end gap-2">
              <MonthInput
                label="From month"
                value={customHorizonDraft.fromMonth}
                invalid={customHorizonError != null}
                describedBy="eex-custom-horizon-message"
                onChange={(value) => changeCustomHorizon("fromMonth", value)}
              />
              <MonthInput
                label="To month (inclusive)"
                value={customHorizonDraft.throughMonth}
                invalid={customHorizonError != null}
                describedBy="eex-custom-horizon-message"
                onChange={(value) => changeCustomHorizon("throughMonth", value)}
              />
              <button
                type="button"
                onClick={applyCustomHorizon}
                disabled={customHorizonError != null || !customHorizonDirty}
                className="h-8 rounded border border-accent/60 bg-accent/10 px-3 text-[10px] font-medium text-accent enabled:hover:bg-accent/15 disabled:cursor-not-allowed disabled:border-border disabled:bg-transparent disabled:text-muted"
              >
                Apply
              </button>
              <button
                type="button"
                onClick={cancelCustomHorizonChanges}
                disabled={!customHorizonDirty || !customHorizonApplied}
                className="h-8 rounded border border-border px-3 text-[10px] text-muted enabled:hover:border-accent/50 enabled:hover:text-fg disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel changes
              </button>
              <div
                id="eex-custom-horizon-message"
                role={customHorizonError ? "alert" : "status"}
                aria-live="polite"
                className={cn(
                  "min-w-48 flex-1 text-[10px]",
                  customHorizonError ? "text-amber-700 dark:text-amber-300" : "text-muted"
                )}
              >
                {customHorizonError ?? customHorizonStatus({
                  announcement: customHorizonAnnouncement,
                  appliedRange: customHorizonApplied,
                  appliedContractCount: appliedCustomPeriods.length,
                  appliedRangeOverflow: appliedCustomRangeOverflow,
                  productLabel: selectedGroup.label,
                  dirty: customHorizonDirty,
                })}
              </div>
            </div>
          </fieldset>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-1.5" aria-label="Forward curve comparison lines">
          <OverlayButton active={overlays.has("live")} onClick={() => toggleOverlay("live")}>
            Live
          </OverlayButton>
          <OverlayButton active={overlays.has("latest")} onClick={() => toggleOverlay("latest")}>
            Latest snapshot
          </OverlayButton>
          <OverlayButton active={overlays.has("fifteen-minutes")} onClick={() => toggleOverlay("fifteen-minutes")}>
            15m ago
          </OverlayButton>
          <OverlayButton active={overlays.has("one-hour")} onClick={() => toggleOverlay("one-hour")}>
            1h ago
          </OverlayButton>
          <OverlayButton active={overlays.has("one-day")} onClick={() => toggleOverlay("one-day")}>
            24h ago
          </OverlayButton>
          <OverlayButton active={overlays.has("one-week")} onClick={() => toggleOverlay("one-week")}>
            1 week ago
          </OverlayButton>
          <OverlayButton active={overlays.has("settlement")} onClick={() => toggleOverlay("settlement")}>
            Previous settlement
          </OverlayButton>
          <OverlayButton active={overlays.has("custom")} onClick={() => toggleOverlay("custom")}>
            Custom snapshot
          </OverlayButton>
          {overlays.has("custom") && (
            <label className="ml-1 flex items-center gap-1.5 text-[10px] text-muted">
              Snapshot at
              <input
                type="datetime-local"
                value={customAt}
                max={toDatetimeLocal(new Date())}
                onChange={(event) => setCustomAt(event.target.value)}
                className="h-8 rounded border border-border bg-transparent px-2 text-xs text-fg focus:border-accent focus:outline-none"
              />
            </label>
          )}
        </div>

        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[9px] text-muted">
          {activeCustomRangeOverflow ? (
            <span>{activeCustomOverflowCount} overlapping contracts · curve hidden until narrowed</span>
          ) : (
            <span>{periods.length} contract{periods.length === 1 ? "" : "s"}</span>
          )}
          {horizon === "front" && <span>Front set 7M · 6Q · 4S · 4Cal</span>}
          {effectiveDomainMonths && (
            <span>Visible delivery {monthRangeLabel(effectiveDomainMonths)}</span>
          )}
          <span>Live and snapshots use {basisLabel(basis)}</span>
          <span>Snapshot points resolve at or before each requested time</span>
          {subjects.length < periods.length && (
            <span>{periods.length - subjects.length} contracts without accessible stored history render gaps</span>
          )}
          {anchorAt && <span>Comparison anchor {formatDateTime(anchorAt)}</span>}
          {horizon !== "custom" && domainPeriodOverflow > 0 && (
            <span className="text-amber-700 dark:text-amber-300">
              Showing the first {EEX_MARKET_DATA_FORWARD_CURVE_SUBJECT_MAX} of {domainPeriodCandidates.length} overlapping contracts; narrow the horizon to include later contracts.
            </span>
          )}
        </div>
      </div>

      {historyQ.isError && (
        <div className="rounded border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[10px] text-amber-700 dark:text-amber-300">
          {historicalErrorMessage(historyQ.error)} Live prices remain available; no historical fallback was fabricated.
        </div>
      )}
      {historyQ.isLoading && needsHistoricalRead && (
        <div className="rounded border border-border px-3 py-2 text-[10px] text-muted">
          Loading stored snapshots and the previous official settlement curve…
        </div>
      )}

      {activeCustomRangeOverflow ? (
        <div className="rounded border border-amber-500/30 bg-amber-500/5 px-3 py-10 text-center text-xs text-amber-700 dark:text-amber-300">
          <div>
            {monthRangeLabel(activeCustomOverflowRange!)} overlaps {activeCustomOverflowCount} {selectedGroup.label} contracts. Custom ranges must contain all contracts, so this curve is not truncated.
          </div>
          <div className="mt-1">Narrow the From and To months to {EEX_MARKET_DATA_FORWARD_CURVE_SUBJECT_MAX} contracts or fewer.</div>
        </div>
      ) : displayDomain && domainPeriodCandidates.length === 0 ? (
        <div className="rounded border border-border px-3 py-10 text-center text-xs text-muted">
          <div>
            No listed {selectedGroup.label} contracts overlap {monthRangeLabel(effectiveDomainMonths!)}.
          </div>
          <div className="mt-3 flex flex-wrap justify-center gap-2">
            <button
              type="button"
              onClick={() => changeHorizon("12m")}
              className="h-8 rounded border border-border px-2.5 text-[10px] hover:border-accent/50 hover:text-fg"
            >
              Show next 12 months
            </button>
            <button
              type="button"
              onClick={() => changeHorizon("all")}
              className="h-8 rounded border border-border px-2.5 text-[10px] hover:border-accent/50 hover:text-fg"
            >
              Show first 64 listed
            </button>
          </div>
        </div>
      ) : (
        <ForwardCurveChart
          productLabel={selectedGroup.label}
          currency={selectedGroup.currency}
          uom={selectedGroup.uom}
          periods={periods}
          series={series}
          displayDomain={displayDomain}
        />
      )}
    </div>
  );
}

function buildChartSeries({
  curveCells,
  basis,
  overlays,
  snapshotComparisons,
  history,
}: {
  curveCells: Array<{
    period: EexMatrixDeliveryPeriod;
    subject: { scopeId: string; subjectId: string } | null;
    cell: {
      instrument: EexMarketDataInstrument;
      quote: EexMarketDataTopOfBookItem | null;
    };
  }>;
  basis: EexForwardCurvePriceBasis;
  overlays: ReadonlySet<OverlayId>;
  snapshotComparisons: Array<{ id: string; requestedAt: string }>;
  history: EexMarketDataForwardCurveResponse | undefined;
}): ForwardCurveChartSeries[] {
  const result: ForwardCurveChartSeries[] = [];
  if (overlays.has("settlement")) {
    const settlementBySubject = new Map(
      (history?.previousSettlement.points ?? []).map((point) => [marketSubjectKey(point), point])
    );
    const tradingDate = history?.previousSettlement.tradingDate ?? null;
    result.push({
      id: "settlement",
      label: tradingDate ? `Previous settlement · ${tradingDate}` : "Previous settlement",
      kind: "settlement",
      dash: "10 4 2 4",
      points: curveCells.map(({ subject }) => {
        const point = subject ? settlementBySubject.get(marketSubjectKey(subject)) : undefined;
        const detail = point?.price == null
          ? [!subject
            ? "Stored settlement history is not accessible for this contract"
            : tradingDate
              ? `No official settlement for ${tradingDate}`
              : "No prior common settlement date"]
          : [
            `Trading date ${tradingDate}`,
            point.source === "ecc_span" ? "ECC SPAN fallback" : "EEX DataSource",
            point.receivedAt ? `received ${formatDateTime(point.receivedAt)}` : "",
          ].filter(Boolean);
        return { value: point?.price ?? null, detail };
      }),
    });
  }

  const comparisonById = new Map(snapshotComparisons.map((comparison) => [comparison.id, comparison]));
  const responseById = new Map((history?.snapshotSeries ?? []).map((line) => [line.id, line]));
  for (const option of SNAPSHOT_OPTIONS) {
    if (!overlays.has(option.id)) continue;
    const comparison = comparisonById.get(option.id);
    const response = responseById.get(option.id);
    const pointBySubject = new Map(
      (response?.points ?? []).map((point) => [marketSubjectKey(point), point])
    );
    result.push({
      id: option.id,
      label: option.label,
      kind: "snapshot",
      dash: option.dash,
      opacity: option.opacity,
      points: curveCells.map(({ cell, subject }) => {
        const point = subject ? pointBySubject.get(marketSubjectKey(subject)) : undefined;
        const value = eexForwardCurvePrice(point, basis);
        const detail = !point || point.gapReason
          ? [!subject
            ? "Stored snapshot history is not accessible for this contract"
            : comparison
              ? `No stored snapshot at or before ${formatDateTime(comparison.requestedAt)}`
              : "Waiting for snapshot comparison"]
          : [
            point.snapshotBoundary ? `snapshot ${formatDateTime(point.snapshotBoundary)}` : "",
            comparison ? `requested at or before ${formatDateTime(comparison.requestedAt)}` : "",
            `${point.coverageState ?? "unknown"} coverage`,
            `${point.freshnessState ?? "unknown"} at capture`,
            value == null ? `no ${basisLabel(basis)} price` : "",
          ].filter(Boolean);
        return { value, detail };
      }),
    });
  }

  if (overlays.has("live")) {
    result.push({
      id: "live",
      label: `Live ${basisLabel(basis)}`,
      kind: "live",
      points: curveCells.map(({ cell }) => {
        const value = eexForwardCurvePrice(cell.quote, basis);
        const detail = !cell.quote
          ? ["No cached live top-of-book observation"]
          : [
            `received ${formatDateTime(cell.quote.receivedAt)}`,
            cell.quote.marketTimestamp
              ? `market ${formatDateTime(cell.quote.marketTimestamp)}`
              : "market time unavailable",
            `${cell.quote.coverageState} coverage`,
            `${cell.quote.freshnessState} feed`,
            value == null ? `no ${basisLabel(basis)} price` : "",
          ].filter(Boolean);
        return { value, detail };
      }),
    });
  }
  return result;
}

function CurveSelect({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1 text-[9px] uppercase tracking-wide text-muted">
      {label}
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-8 min-w-44 rounded border border-border bg-bg px-2 text-xs normal-case tracking-normal text-fg focus:border-accent focus:outline-none"
      >
        {children}
      </select>
    </label>
  );
}

function MonthInput({
  label,
  value,
  invalid,
  describedBy,
  onChange,
}: {
  label: string;
  value: string;
  invalid: boolean;
  describedBy: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex min-w-36 flex-1 flex-col gap-1 text-[9px] uppercase tracking-wide text-muted sm:max-w-48">
      {label}
      <input
        type="month"
        required
        value={value}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        onChange={(event) => onChange(event.target.value)}
        className="h-8 w-full rounded border border-border bg-bg px-2 text-xs normal-case tracking-normal text-fg focus:border-accent focus:outline-none"
      />
    </label>
  );
}

function OverlayButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "h-8 rounded border px-2 text-[10px] transition-colors",
        active
          ? "border-accent/60 bg-accent/10 font-medium text-accent"
          : "border-border text-muted hover:border-accent/50 hover:text-fg"
      )}
    >
      {children}
    </button>
  );
}

function marketSubjectKey(row: { scopeId: string; subjectId: string }): string {
  return `${row.scopeId}\u0000${row.subjectId}`;
}

function basisLabel(basis: EexForwardCurvePriceBasis): string {
  if (basis === "midpoint") return "midpoint";
  if (basis === "last") return "last trade";
  return basis;
}

function customHorizonValidationMessage(
  range: CustomHorizonRange,
  domain: EexForwardCurveTimeDomain | null,
  contractCount: number
): string | null {
  if (!range.fromMonth || !range.throughMonth) {
    return "Choose both a From month and a To month before applying this horizon.";
  }
  if (!domain) {
    return /^\d{4}-\d{2}$/.test(range.fromMonth)
      && /^\d{4}-\d{2}$/.test(range.throughMonth)
      && range.fromMonth > range.throughMonth
      ? "Start month must be no later than end month."
      : "Choose valid delivery months before applying this horizon.";
  }
  if (contractCount > EEX_MARKET_DATA_FORWARD_CURVE_SUBJECT_MAX) {
    return `This range contains ${contractCount} contracts; narrow it to ${EEX_MARKET_DATA_FORWARD_CURVE_SUBJECT_MAX} or fewer.`;
  }
  return null;
}

function customHorizonStatus({
  announcement,
  appliedRange,
  appliedContractCount,
  appliedRangeOverflow,
  productLabel,
  dirty,
}: {
  announcement: string;
  appliedRange: CustomHorizonRange | null;
  appliedContractCount: number;
  appliedRangeOverflow: boolean;
  productLabel: string;
  dirty: boolean;
}): string {
  if (!appliedRange) {
    return announcement || "End month is inclusive. Contracts crossing either edge remain visible.";
  }
  const rangeLabel = monthRangeLabel(appliedRange);
  const contractNoun = `${productLabel} contract${appliedContractCount === 1 ? "" : "s"}`;
  const contracts = `${appliedContractCount} overlapping ${contractNoun}`;
  if (appliedRangeOverflow) {
    return dirty
      ? `Editing an unapplied range. ${rangeLabel} overlaps ${appliedContractCount} ${contractNoun}; the curve is hidden until a valid range is applied.`
      : `${rangeLabel} overlaps ${appliedContractCount} ${contractNoun}; the curve is hidden until the range is narrowed.`;
  }
  const current = `${rangeLabel} contains ${contracts}.`;
  if (dirty) return `Editing an unapplied range. The chart still shows ${rangeLabel}, with ${contracts}.`;
  return [announcement, current].filter(Boolean).join(" ");
}

function monthRangeLabel(range: CustomHorizonRange): string {
  const from = monthLabel(range.fromMonth);
  const through = monthLabel(range.throughMonth);
  return range.fromMonth === range.throughMonth ? from : `${from}–${through}`;
}

function monthLabel(value: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(value);
  if (!match) return value;
  const parsed = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1));
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(parsed);
}

function historicalErrorMessage(error: Error): string {
  if (error instanceof ApiError && error.status === 422) {
    return "Stored history is not enabled for every contract in this curve.";
  }
  return `Historical curve comparisons could not be loaded: ${error.message}.`;
}

function formatDateTime(value: string): string {
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(parsed);
}

function toDatetimeLocal(value: Date): string {
  const local = new Date(value.getTime() - value.getTimezoneOffset() * 60 * 1_000);
  return local.toISOString().slice(0, 16);
}
